/**
 * DICOM Metadata Loader — canonical (HTJ2K) mode
 *
 * Provides functions for loading DICOM images from the canonical bucket
 * (a2z-canonical) via CloudFront. Backend returns one `canonical_cdn_base_url`
 * per response; each instance carries its `s3_key`. Frontend concatenates
 * them and feeds them to Cornerstone via the 'wadouri:' loader.
 *
 * Usage:
 * 1. Call fetchStudyMetadata() (or fetchStudyImages() on a page with its own
 *    images route) to get the canonical metadata (throws StudyNotCanonicalizedError on 423).
 * 2. Use buildS3ImageIds() with the response's canonical_cdn_base_url
 *    and per-instance s3_key to create Cornerstone imageIds.
 */

import { utilities as csUtilities } from "@cornerstonejs/core";
import { canonicalObjectUrl, registerCdnToken } from "./cdnTokens";
import { getApiBaseUrl } from "../config/api";
import { logger } from "@/utils/logger";
import { seriesRegionRank } from "./seriesClassification";
import { normalizeVoiLutMetadata } from "./dicomVoi";

// =============================================================================
// Types
// =============================================================================

/**
 * Information about a single DICOM instance
 */
export interface DicomInstanceInfo {
  instance_uid: string; // DICOM SOPInstanceUID
  instance_number: number; // DICOM InstanceNumber (not used for sorting - backend sorts by z_position)
  study_uid?: string; // DICOM StudyInstanceUID; older canonical metadata omits it
  rows?: number; // Image rows (pixels)
  columns?: number; // Image columns (pixels)
  bits_allocated?: number; // Bits allocated per pixel (8 or 16)
  bits_stored?: number; // Bits actually used (typically equals bits_allocated)
  photometric_interpretation?: string; // e.g., MONOCHROME2
  pixel_representation?: number; // 0=unsigned, 1=signed
  transfer_syntax_uid?: string; // Encoding (can vary per-instance within a series)
  s3_key: string; // Canonical-bucket key — frontend builds `<canonical_cdn_base_url>/<s3_key>` to fetch
  // Per-instance rescale values (from dicom_utils.extract_instance_metadata)
  // These take precedence over series-level values for correct rendering
  rescale_slope?: number | null; // Modality LUT slope (null if not in DICOM)
  rescale_intercept?: number | null; // Modality LUT intercept (null if not in DICOM)
  z_position?: number; // Computed slice position for anatomical sorting
  image_position_patient?: number[] | null; // 3-vector [x,y,z]; needed by Cornerstone to map world→slice for annotation rendering
  pixel_spacing?: string | null;
  image_orientation_patient?: number[] | null;
}

/**
 * Series-level metadata for hanging protocol assignment
 */
export interface SeriesMetadata {
  series_uid: string;
  series_description: string | null;
  series_number: number;
  modality: string;
  plane: "Axial" | "Coronal" | "Sagittal" | null;
  has_contrast: boolean;
  contrast_agent: string | null;
  body_part: string | null;
  num_instances: number;
  window_center: number | null;
  window_width: number | null;
  slice_thickness: number | null;
  pixel_spacing: string | null; // Format: "0.5\\0.5"
  rescale_slope: number | null; // Modality LUT slope (default: 1.0)
  rescale_intercept: number | null; // Modality LUT intercept (default: 0.0)
  frame_of_reference_uid: string | null; // FrameOfReferenceUID for spatial alignment validation
  acquisition_number: number | null; // AcquisitionNumber — identifies which scanner pass
  image_orientation_patient: number[] | null; // 6-element [row_x, row_y, row_z, col_x, col_y, col_z]; used to derive plane when `plane` is missing
  // Volume-vs-MPR predicate from canonical metadata. Source: OHIF's
  // isDisplaySetReconstructable, ported to Python in the backend
  // (src/is_reconstructable.py) and stamped at the canonical_metadata.fetch
  // boundary. True → VolumeViewport supports MPR / segmentation / region-grow.
  // False → single-instance / 2D / geometrically degenerate; VolumeViewport
  // can still render the native acquisition plane but MPR-dependent surfaces
  // (PhaseDependentLayout phase tabs, dicomMetadataLoader imagePlaneModule)
  // skip these and defer to wadouri. `reconstruction_blocker` carries the
  // diagnostic reason.
  is_reconstructable: boolean;
  reconstruction_blocker:
    | "no_instances"
    | "single_instance"
    | "missing_ipp"
    | "missing_iop"
    | "missing_pixel_spacing"
    | "missing_for"
    | "inconsistent_dimensions"
    | "inconsistent_iop"
    | "irregular_spacing"
    | "degenerate_ipp"
    | null;
  average_spacing_mm: number | null;
}

/**
 * A study's canonical images, from /api/get_study_metadata or a device panel's
 * /images route (which names no job).
 */
export interface StudyMetadataResponse {
  canonical_cdn_base_url: string; // CloudFront origin for canonical bytes; combine with s3_key
  canonical_cdn_token?: string; // study-scoped CDN token; see cdnTokens.ts
  series: Record<string, DicomInstanceInfo[]>;
  series_metadata: Record<string, SeriesMetadata>;
}

/**
 * Thrown by fetchStudyMetadata when the backend returns 423 because the
 * study hasn't been migrated to canonical storage yet. Callers can branch
 * on `instanceof StudyNotCanonicalizedError` and render the "Migration
 * pending" panel rather than treating it as a generic fetch failure.
 */
export class StudyNotCanonicalizedError extends Error {
  readonly code = "study_not_canonicalized" as const;
  constructor(message: string) {
    super(message);
    this.name = "StudyNotCanonicalizedError";
  }
}

// =============================================================================
// Series-level helpers
// =============================================================================

/**
 * DICOM PS3.3 C.7.6.3.1.2: when PhotometricInterpretation is MONOCHROME1, the
 * minimum sample value is meant to display as white. The viewer must invert
 * the rendered output for normal display. Affects some Secondary Capture and
 * scout/topogram series.
 */
export function shouldAutoInvert(instances: DicomInstanceInfo[] | undefined): boolean {
  return instances?.[0]?.photometric_interpretation === "MONOCHROME1";
}

/**
 * Pick the series to auto-load when there's no model selection. Prefers
 * abdomen-region series and pushes brain/head series to the bottom — relevant
 * for body-focused projects (e.g. urinary-stone studies) that incidentally
 * include a head/brain series. Falls back to the first available series when
 * metadata is missing or no signal is present.
 */
export function pickPreferredSeries(
  availableSeries: string[],
  seriesMetadata: Record<string, SeriesMetadata> | undefined,
): string | undefined {
  if (availableSeries.length === 0) return undefined;
  if (!seriesMetadata) return availableSeries[0];
  // Lower rank wins (body=0 > neutral=1 > head=2). Same classifier as
  // hangingProtocolService so auto-load and slot assignment agree on order.
  return [...availableSeries].sort((a, b) => {
    const ma = seriesMetadata[a];
    const mb = seriesMetadata[b];
    const ra = ma ? seriesRegionRank(ma) : 1;
    const rb = mb ? seriesRegionRank(mb) : 1;
    return ra - rb;
  })[0];
}

// =============================================================================
// API Functions
// =============================================================================

/**
 * Fetch study metadata for DICOM series/instance info.
 *
 * Returns series and instance information needed to build Cornerstone imageIds.
 *
 * @param userId - User email address
 * @param studyId - Study identifier (may be compound: study_id::created_at)
 * @param token - JWT auth token
 * @returns Study metadata response
 */
export async function fetchStudyMetadata(
  userId: string,
  studyId: string,
  token: string,
): Promise<StudyMetadataResponse> {
  const apiBaseUrl = getApiBaseUrl();

  const response = await fetch(`${apiBaseUrl}/api/get_study_metadata`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ user_id: userId, study_id: studyId }),
  });
  return readStudyImages(response);
}

/**
 * Fetch a study's images through a page's own images route (a device panel's or
 * a joint review's), with that route's access, so the page never needs the
 * caller's access to a device's job. Same response and errors as
 * fetchStudyMetadata.
 */
export async function fetchStudyImages(
  imagesUrl: string,
  token: string,
): Promise<StudyMetadataResponse> {
  const response = await fetch(imagesUrl, { headers: { Authorization: `Bearer ${token}` } });
  return readStudyImages(response);
}

async function readStudyImages(response: Response): Promise<StudyMetadataResponse> {
  if (response.ok) {
    const data: StudyMetadataResponse = await response.json();
    registerCdnToken(data.canonical_cdn_token);
    return data;
  }

  // 423 on either route is structurally `study_not_canonicalized` —
  // no need to re-verify detail.error. Surface as a typed error so React
  // Query surfaces it via `error` and the viewers render "Migration
  // pending" instead of an indefinite spinner.
  if (response.status === 423) {
    let message = "Study not migrated to canonical storage.";
    try {
      const body = await response.json();
      if (typeof body?.detail?.message === "string") message = body.detail.message;
    } catch {
      // Non-JSON body — keep the default message.
    }
    throw new StudyNotCanonicalizedError(message);
  }

  let detail: unknown = null;
  try {
    detail = (await response.json())?.detail;
  } catch {
    // Non-JSON body — the status alone says what failed.
  }
  throw new Error(
    typeof detail === "string" ? detail : `Failed to fetch study metadata: HTTP ${response.status}`,
  );
}

// =============================================================================
// S3 DICOM Loading (via wadouri scheme)
// =============================================================================

/**
 * Build wadouri imageIds for canonical-bucket DICOM loading.
 *
 * Backend returns one `canonical_cdn_base_url` per response (the CloudFront
 * distribution domain) and `s3_key` on every instance. We concatenate them.
 * Browser fetches directly from CloudFront — no per-URL signing, no backend
 * proxy hop. Discovery is gated by demo-backend auth; DICOM UIDs in the
 * path are ~200 bits of entropy. See web-demo-backend#258.
 *
 * @param cdnBaseUrl - CloudFront origin (e.g. https://d123.cloudfront.net)
 * @param instances - List of instance info from backend (includes s3_key, pre-sorted by z_position)
 * @returns Array of Cornerstone imageIds using 'wadouri:' scheme
 */
export function buildS3ImageIds(cdnBaseUrl: string, instances: DicomInstanceInfo[]): string[] {
  if (!cdnBaseUrl) {
    throw new Error(
      "canonical_cdn_base_url missing from study metadata — backend response is malformed",
    );
  }

  // Instances arrive pre-sorted by z_position from backend (anatomical order).
  // Do NOT re-sort — backend uses ImagePositionPatient projection for correct ordering.
  return instances.map((instance) => `wadouri:${canonicalObjectUrl(cdnBaseUrl, instance.s3_key)}`);
}

// =============================================================================
// imagePlaneModule metadata pre-population
// =============================================================================
// Cornerstone needs ImagePositionPatient + IOP + spacing per imageId to map
// world points to slice indices (annotation rendering, slice selection). The
// built-in wadouri provider only fills these once a slice's bytes are streamed,
// which doesn't help for region-grow propagation that touches un-streamed
// slices. We pre-populate cornerstone's first-class genericMetadataProvider
// from study metadata so any provider call resolves without waiting on bytes.
//
// Note: `pixelSpacing` must be a tuple — cornerstone's makeVolumeMetadata /
// generateVolumePropsFromImageIds indexes [1]/[0] on it. Omitting this caused
// "Cannot read properties of undefined (reading '1')" during VolumeViewport
// setup, which in turn produced the "Rendered fewer hooks" React error.

function parsePixelSpacing(s: string | null | undefined): [number, number] | null {
  if (!s) return null;
  const parts = s
    .split(/[\\/,;\s]+/)
    .filter(Boolean)
    .map(Number);
  if (parts.length < 2 || parts.some((n) => !Number.isFinite(n))) return null;
  return [parts[0], parts[1]];
}

/**
 * Populate the imagePlaneModule cache for every imageId in a series. Call this
 * once per series after fetching study metadata, before Cornerstone needs IPP
 * for annotation/world<->slice math.
 */
export function registerImagePlaneMetadataForSeries(
  cdnBaseUrl: string,
  instances: DicomInstanceInfo[],
  seriesMeta: SeriesMetadata | undefined,
): void {
  if (!cdnBaseUrl) return;
  // The eager imagePlaneModule cache is an OPTIONAL fast-path — the DICOM
  // bytes are the single source of truth for IPP/IOP/spacing, and wadouri
  // reads them per-instance when it streams the file. When the cached
  // metadata is non-reconstructable or partial we delegate to that
  // canonical path instead of shadowing it with stale/inconsistent
  // pre-cache entries. This is NOT a silent fallback (no_silent_fallbacks
  // rule) — wadouri remains the authoritative source either way; the
  // pre-cache is purely an "if we can, do it eagerly" optimisation that
  // we explicitly skip when its inputs aren't trustworthy.
  const deferToWadouri = (reason: string, extra: Record<string, unknown>) => {
    logger.warn(
      `[dicomMetadataLoader] deferring imagePlaneModule to wadouri (DICOM bytes): ${reason}`,
      { seriesUid: seriesMeta?.series_uid, instanceCount: instances.length, ...extra },
    );
  };
  if (seriesMeta && seriesMeta.is_reconstructable === false) {
    deferToWadouri("non-reconstructable series", {
      blocker: seriesMeta.reconstruction_blocker,
    });
    return;
  }
  const iop = seriesMeta?.image_orientation_patient;
  const pxSpacing = parsePixelSpacing(seriesMeta?.pixel_spacing);
  const frameOfReferenceUID = seriesMeta?.frame_of_reference_uid;
  if (!iop || iop.length < 6 || !pxSpacing || !frameOfReferenceUID) {
    deferToWadouri("partial series metadata", {
      hasIop: !!iop && iop.length >= 6,
      hasPixelSpacing: !!pxSpacing,
      hasFor: !!frameOfReferenceUID,
    });
    return;
  }
  const rowCosines: [number, number, number] = [iop[0], iop[1], iop[2]];
  const columnCosines: [number, number, number] = [iop[3], iop[4], iop[5]];
  // Slice normal = row × col (both unit cosines → normal is unit). Used to
  // synthesize IPP from z_position for instances whose IPP wasn't stamped
  // into metadata.json. z_position = IPP_real · normal by construction, so
  // IPP_synth = z_position * normal lies on the same projected line —
  // Cornerstone's slice math (normal-projection-based) stays consistent.
  // CAVEAT: synthesized IPP lands on the normal axis through the origin, not
  // the lesion's real XY. Annotations placed against a synthesized IPP can't
  // round-trip jumpToWorld(centroid) accurately, but they render correctly
  // within the slice itself.
  const normal: [number, number, number] = [
    rowCosines[1] * columnCosines[2] - rowCosines[2] * columnCosines[1],
    rowCosines[2] * columnCosines[0] - rowCosines[0] * columnCosines[2],
    rowCosines[0] * columnCosines[1] - rowCosines[1] * columnCosines[0],
  ];
  // Surface degenerate IPP instead of silently synthesizing it (no_silent_fallbacks).
  // The web-demo-backend#271 fingerprint = real image_position_patient missing +
  // only z_position present; we synthesize IPP = z_position*normal, which keeps
  // in-slice rendering correct but collapses the true XY → cross-plane alignment
  // and saved-mask placement degrade. CONSTANT z across slices is the unambiguous
  // failure (every companion slice maps to one world plane). The real fix is
  // backend (stamp real IPP / re-canonicalize); this warn makes it traceable in
  // production logs rather than silently misplacing annotations.
  const synthInstances = instances.filter(
    (i) =>
      (!i.image_position_patient || i.image_position_patient.length < 3) &&
      typeof i.z_position === "number",
  );
  if (synthInstances.length > 0) {
    const zs = synthInstances.map((i) => i.z_position as number);
    const zConstant = zs.length > 1 && Math.max(...zs) - Math.min(...zs) < 1e-6;
    logger.warn(
      "[dicomMetadataLoader] viewer_ipp_synthesized seriesUid=%s synthesized=%d/%d constantZ=%s — image_position_patient missing from metadata; cross-plane alignment + saved-mask placement may be wrong (backend web-demo-backend#271).%s",
      seriesMeta?.series_uid,
      synthInstances.length,
      instances.length,
      zConstant,
      zConstant ? " CONSTANT z across slices → companion-plane mapping is degenerate." : "",
    );
  }
  for (const inst of instances) {
    if (inst.rows == null || inst.columns == null) continue;
    let ipp = inst.image_position_patient ?? null;
    if ((!ipp || ipp.length < 3) && typeof inst.z_position === "number") {
      ipp = [normal[0] * inst.z_position, normal[1] * inst.z_position, normal[2] * inst.z_position];
    }
    if (!ipp || ipp.length < 3) {
      // No IPP and no z_position — wadouri loader will pull from .dcm header
      // when the instance is fetched. Skip eager cache for this one.
      continue;
    }
    const imageId = `wadouri:${canonicalObjectUrl(cdnBaseUrl, inst.s3_key)}`;
    csUtilities.genericMetadataProvider.add(imageId, {
      type: "imagePlaneModule",
      metadata: {
        frameOfReferenceUID,
        rows: inst.rows,
        columns: inst.columns,
        rowCosines,
        columnCosines,
        imageOrientationPatient: iop,
        imagePositionPatient: ipp,
        pixelSpacing: pxSpacing,
        rowPixelSpacing: pxSpacing[0],
        columnPixelSpacing: pxSpacing[1],
        sliceThickness: seriesMeta?.slice_thickness ?? undefined,
      },
    });
  }
}

/**
 * Populate the imagePixelModule cache for every imageId in a series. Required
 * for canonical/HTJ2K volumes — Cornerstone's `getStreamingImageVolume` reads
 * `pixelRepresentation` (and friends) at volume-construction time via
 * `metaData.get("imagePixelModule", imageId)` to pick the typed-array
 * constructor for the volume buffer. The wadouri provider only fills these
 * after streaming a slice's bytes, which is too late — the destructure
 * `const { pixelRepresentation } = metaData.get(...)` throws on `undefined`
 * inside `makeVolumeMetadata`'s hook body, which React surfaces as the
 * generic "Rendered fewer hooks than expected" error. Call alongside
 * `registerImagePlaneMetadataForSeries` from the case-viewer load path.
 *
 * Source fields come straight from `metadata.json` (captured at canonicalize
 * time by `dicom_utils.extract_instance_metadata`); the backend passes the
 * per-instance dicts through as `Dict[str, Any]` so the values land on
 * `DicomInstanceInfo` without an explicit Pydantic mapping.
 */
export function registerImagePixelMetadataForSeries(
  cdnBaseUrl: string,
  instances: DicomInstanceInfo[],
): void {
  if (!cdnBaseUrl) return;
  for (const inst of instances) {
    // Required fields for the volume builder; skip the entry rather than
    // register a partial one (a partial entry shadows the wadouri provider
    // and crashes the volume builder, same pattern as imagePlaneModule above).
    if (
      inst.bits_allocated == null ||
      inst.bits_stored == null ||
      inst.pixel_representation == null
    ) {
      continue;
    }
    const imageId = `wadouri:${canonicalObjectUrl(cdnBaseUrl, inst.s3_key)}`;
    csUtilities.genericMetadataProvider.add(imageId, {
      type: "imagePixelModule",
      metadata: {
        bitsAllocated: inst.bits_allocated,
        bitsStored: inst.bits_stored,
        highBit: inst.bits_stored - 1,
        pixelRepresentation: inst.pixel_representation,
        samplesPerPixel: 1,
        photometricInterpretation: inst.photometric_interpretation ?? "MONOCHROME2",
      },
    });
  }
}

/**
 * Populate the generalSeriesModule cache for every imageId in a series.
 * `makeVolumeMetadata` destructures `modality` and `seriesInstanceUID` from
 * the result without null-checking, so an undefined return crashes the
 * volume builder. Same pre-stream gap as imagePixelModule / imagePlaneModule.
 */
export function registerGeneralSeriesMetadataForSeries(
  cdnBaseUrl: string,
  instances: DicomInstanceInfo[],
  seriesMeta: SeriesMetadata | undefined,
): void {
  if (!cdnBaseUrl) return;
  const modality = seriesMeta?.modality;
  const seriesInstanceUID = seriesMeta?.series_uid;
  if (!modality || !seriesInstanceUID) return;
  for (const inst of instances) {
    const imageId = `wadouri:${canonicalObjectUrl(cdnBaseUrl, inst.s3_key)}`;
    csUtilities.genericMetadataProvider.add(imageId, {
      type: "generalSeriesModule",
      metadata: { modality, seriesInstanceUID },
    });
  }
}

/**
 * Populate the voiLutModule cache for every imageId in a series. Optional
 * for the volume builder (the makeVolumeMetadata source guards with `if
 * (voiLutModule)`), but registering it gives the viewport a real default
 * window/level instead of cornerstone's auto-pick from min/max pixel.
 */
export function registerVoiLutMetadataForSeries(
  cdnBaseUrl: string,
  instances: DicomInstanceInfo[],
  seriesMeta: SeriesMetadata | undefined,
): void {
  if (!cdnBaseUrl) return;
  const windowCenter = seriesMeta?.window_center;
  const windowWidth = seriesMeta?.window_width;
  if (windowCenter == null || windowWidth == null) return;
  const metadata = normalizeVoiLutMetadata({ windowCenter, windowWidth });
  for (const inst of instances) {
    const imageId = `wadouri:${canonicalObjectUrl(cdnBaseUrl, inst.s3_key)}`;
    csUtilities.genericMetadataProvider.add(imageId, {
      type: "voiLutModule",
      metadata,
    });
  }
}
